import { Modal, Panel } from '@elotech/components';
import { FormikProps, connect } from 'formik';
import React, { useMemo, useState } from 'react';

import styles from './IconeSelect.module.css';

interface IconeDefinition {
  iconName?: string;
  prefix?: string;
}

interface Props {
  name: string;
  label?: string;
  value?: string;
  icones: Record<string, IconeDefinition>;
  disabled?: boolean;
  onChange: (icone: string) => void;
}

const classeDoIcone = (icone: IconeDefinition): string =>
  `${icone.prefix || 'fas'} fa-${icone.iconName}`;

export const IconeSelect: React.FC<Props> = ({
  name,
  label = 'Ícone',
  value,
  icones,
  disabled = false,
  onChange
}) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  const todos = useMemo(
    () => Object.values(icones).filter(icone => !!icone?.iconName),
    [icones]
  );

  const disponiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return termo
      ? todos.filter(icone => icone.iconName!.toLowerCase().includes(termo))
      : todos;
  }, [todos, busca]);

  const selecionado = useMemo(
    () => todos.find(icone => classeDoIcone(icone) === value),
    [todos, value]
  );

  const abrir = () => {
    setBusca('');
    setAberto(true);
  };

  const selecionar = (icone: IconeDefinition) => {
    onChange(classeDoIcone(icone));
    setAberto(false);
  };

  return (
    <div
      className={`form-group ${styles['icone-select']}`}
      data-testid={`icone-select-${name}`}
    >
      <label className="label">{label}</label>

      <div className={styles['icone-select-campo-wrapper']}>
        <button
          type="button"
          className={`form-input ${styles['icone-select-campo']}`}
          disabled={disabled}
          onClick={abrir}
        >
          {value ? (
            <span className={styles['icone-select-valor']}>
              <i aria-hidden="true" className={value} />
              {selecionado?.iconName ?? value}
            </span>
          ) : (
            <span className={styles['icone-select-placeholder']}>
              Selecionar ícone
            </span>
          )}

          <i aria-hidden="true" className="fa fa-chevron-down" />
        </button>

        {!!value && !disabled && (
          <button
            type="button"
            title="Remover ícone"
            className={styles['icone-select-limpar']}
            onClick={() => onChange('')}
          >
            <i aria-hidden="true" className="fa fa-times" />
          </button>
        )}
      </div>

      {aberto && (
        <Modal onClose={() => setAberto(false)} maxWidth="640px">
          <Panel isTable={false} title="Selecionar ícone">
            <input
              autoFocus
              type="text"
              className="form-input"
              placeholder="Buscar ícone..."
              value={busca}
              onChange={event => setBusca(event.target.value)}
            />

            <div className={styles['icone-select-grid']}>
              {disponiveis.map(icone => {
                const classe = classeDoIcone(icone);

                return (
                  <button
                    key={classe}
                    type="button"
                    title={icone.iconName}
                    className={`${styles['icone-select-item']}${
                      value === classe ? ` ${styles['selected']}` : ''
                    }`}
                    onClick={() => selecionar(icone)}
                  >
                    <i aria-hidden="true" className={classe} />
                  </button>
                );
              })}

              {disponiveis.length === 0 && (
                <span className={styles['icone-select-empty']}>
                  Nenhum ícone encontrado.
                </span>
              )}
            </div>
          </Panel>
        </Modal>
      )}
    </div>
  );
};

type FormikIconeSelectProps = Omit<Props, 'value' | 'onChange'> & {
  formik: FormikProps<any>;
};

const FormikIconeSelectComponent: React.FC<FormikIconeSelectProps> = ({
  formik,
  name,
  ...props
}) => (
  <IconeSelect
    {...props}
    name={name}
    value={formik.values[name]}
    onChange={icone => formik.setFieldValue(name, icone)}
  />
);

export const FormikIconeSelect = connect<
  Omit<FormikIconeSelectProps, 'formik'>
>(FormikIconeSelectComponent);
